import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexto/AuthContexto';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Wrench, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
  const { iniciarSesion } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mostrarPwd, setMostrarPwd] = useState(false);
  const [cargando, setCargando] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Ingresa tu email y contrasena');
      return;
    }
    setCargando(true);
    try {
      const usr = await iniciarSesion(email.trim(), password);
      toast.success(`Bienvenido, ${usr.nombre}`);
      if (usr.rol === 'super_admin') navigate('/admin');
      else if (usr.tipo_negocio === 'carwash') navigate('/carwash');
      else navigate('/dashboard');
    } catch (err) {
      // Check if it's an unverified email response (200 but no token)
      const data = err?.response?.data;
      if (data?.email_no_verificado) {
        toast.error('Debes verificar tu correo electronico');
        navigate(`/verificacion-pendiente?email=${encodeURIComponent(data.email)}&uid=${data.user_id}`);
        return;
      }
      const detail = data?.detail || '';
      if (detail.includes('Demasiados intentos')) {
        toast.error(detail);
      } else {
        toast.error('Credenciales incorrectas');
      }
    } finally {
      setCargando(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      data-testid="login-page"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1691840204491-f5c608613b68?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMG1vZGVybiUyMGF1dG8lMjByZXBhaXIlMjBzaG9wJTIwaW50ZXJpb3IlMjBkYXJrfGVufDB8fHx8MTc3MzQxODQ4M3ww&ixlib=rb-4.1.0&q=85)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      <div className="relative z-10 w-full max-w-md mx-4 animate-fadeIn">
        <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-md p-8 shadow-2xl">
          <div className="flex flex-col items-center mb-8">
            <div className="w-14 h-14 rounded-sm bg-amber-500/20 flex items-center justify-center mb-4">
              <Wrench className="w-8 h-8 text-amber-500" strokeWidth={1.5} />
            </div>
            <h1 className="font-barlow text-3xl font-bold text-amber-500 uppercase tracking-wider">
              Talleres PRO
            </h1>
            <p className="text-slate-400 text-sm mt-1">Gestion de talleres automotrices</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300 text-xs uppercase tracking-wider">
                Correo electronico
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                className="bg-slate-950 border-slate-700 focus:border-amber-500 h-11 text-slate-200 placeholder:text-slate-600"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300 text-xs uppercase tracking-wider">
                Contrasena
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={mostrarPwd ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Tu contrasena"
                  className="bg-slate-950 border-slate-700 focus:border-amber-500 h-11 text-slate-200 placeholder:text-slate-600 pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setMostrarPwd(!mostrarPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  data-testid="toggle-password-btn"
                >
                  {mostrarPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={cargando}
              className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm shadow-[0_0_15px_rgba(245,158,11,0.3)] hover:shadow-[0_0_25px_rgba(245,158,11,0.5)] transition-all"
              data-testid="login-submit-btn"
            >
              {cargando ? 'Iniciando sesion...' : 'Iniciar Sesion'}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-2">
            <Link to="/recuperar-password" className="text-sm text-slate-500 hover:text-amber-500 transition-colors" data-testid="login-recuperar-link">
              Olvidaste tu contrasena?
            </Link>
            <p className="text-sm text-slate-400">
              No tienes cuenta?{' '}
              <Link to="/registro" className="text-amber-500 hover:text-amber-400 font-medium" data-testid="login-registro-link">Registrate gratis</Link>
            </p>
            <Link to="/" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300">
              <ArrowLeft className="w-3 h-3" /> Volver al inicio
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
